import '.go{display:inline-block;}.ssr-plugin'
import '.gp{padding-right:4px;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.gq{flex-wrap:nowrap;}.ssr-plugin'
import '.bc{overflow:hidden;}.ssr-plugin'
import '.ek{flex-shrink:0;}.ssr-plugin'
import '.gr{position:sticky;}.ssr-plugin'
import '.ac{flex-direction:column;}.ssr-plugin'
import '.fa{gap:6px;}.ssr-plugin'
import '.dj{top:0px;}.ssr-plugin'
import '.gs{padding:8px 8px 0px 8px;}.ssr-plugin'
import '.dt{z-index:1;}.ssr-plugin'
import '.fk{padding:8px;}.ssr-plugin'
import '.eo{gap:2px;}.ssr-plugin'
import '.aw{bottom:0px;}.ssr-plugin'
import '.gt{padding:0px 8px 8px 8px;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import { ReactNode, useState, useRef, useEffect, useLayoutEffect } from 'react'
import { IconProps } from '../Icon/index.js'
import { Menu } from '../Menu/index.js'
import { TextInput } from '../TextInput/index.js'
import { Separator } from '../Separator/index.js'
import { Dropdown } from '../Dropdown/index.js'
import { Badge, BadgeColors } from '../Badge/index.js'
import { Style, styled } from 'inlines'
import { colors } from '../../tokens/colors.js'
import { LabelDescription } from '../LabelDescription/index.js'

type MultiSelectInputOption = {
  value: string | number | boolean
  label: string | ReactNode
  labelFilterText?: string
  color?: BadgeColors
}

type MultiSelectInputProps = {
  values?: (string | number | boolean)[]
  onChange: (values: (string | number | boolean)[]) => void
  leadIcon?: IconProps['variant']
  error?: boolean
  disabled?: boolean
  placeholder?: string
  options: MultiSelectInputOption[]
  filterable?: boolean
  style?: Style
  label?: string
  description?: string
  errorMessage?: string
}

const OverflowBadge = ({ count }: { count: number }) => (
  <div className="go gp">
    <Badge color="neutral-subtle">+{count}</Badge>
  </div>
)

const MultiSelectInput = ({
  disabled,
  error,
  leadIcon,
  options,
  values,
  onChange,
  placeholder,
  filterable,
  style,
  label,
  description,
  errorMessage,
}: MultiSelectInputProps) => {
  const [filter, setFilter] = useState<string>()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLButtonElement>(null)
  const [visibleBadgeCount, setVisibleBadgeCount] = useState<number | null>(
    null,
  )

  const shouldBeFilterable =
    options.length > 5 ? filterable !== false : filterable

  if (!values) {
    values = []
  }

  const selectedOptions = options.filter((option) =>
    values.includes(option.value),
  )

  useLayoutEffect(() => {
    if (!dropdownRef.current) return

    const dropdownElement = dropdownRef.current
    const dropdownWidth = dropdownElement.offsetWidth

    const baseHorizontalPadding = 16
    const chevronIconWidth = 24
    const leadIconCalculatedWidth = leadIcon ? 24 : 0

    const estimatedNonContentWidth =
      baseHorizontalPadding + chevronIconWidth + leadIconCalculatedWidth
    const availableWidth = dropdownWidth - estimatedNonContentWidth

    let currentWidth = 0
    let count = 0

    const tempDiv = document.createElement('div')
    tempDiv.style.position = 'absolute'
    tempDiv.style.visibility = 'hidden'
    tempDiv.style.whiteSpace = 'nowrap'
    document.body.appendChild(tempDiv)

    const badgeWidths: { [key: string]: number } = {}
    const badgeGap = 4

    selectedOptions.forEach((option) => {
      const badgeText =
        typeof option.label === 'string' ? option.label : String(option.value)
      const tempBadgeWrapper = document.createElement('div')
      tempBadgeWrapper.style.display = 'inline-block'
      tempBadgeWrapper.style.paddingRight = `${badgeGap}px`

      const tempBadgeContent = document.createElement('span')
      tempBadgeContent.style.display = 'inline-block'
      tempBadgeContent.style.boxSizing = 'border-box'
      tempBadgeContent.style.borderRadius = '4px'
      tempBadgeContent.style.padding = '2px 8px'
      tempBadgeContent.style.fontSize = '12px'
      tempBadgeContent.style.lineHeight = '1'
      tempBadgeContent.textContent = badgeText
      tempBadgeWrapper.appendChild(tempBadgeContent)

      tempDiv.appendChild(tempBadgeWrapper)
      badgeWidths[option.value.toString()] = tempBadgeWrapper.offsetWidth
      tempDiv.removeChild(tempBadgeWrapper)
    })

    document.body.removeChild(tempDiv)

    const tempOverflowBadgeWrapper = document.createElement('div')
    tempOverflowBadgeWrapper.style.display = 'inline-block'
    tempOverflowBadgeWrapper.style.paddingRight = `${badgeGap}px`

    const tempOverflowBadgeContent = document.createElement('span')
    tempOverflowBadgeContent.style.display = 'inline-block'
    tempOverflowBadgeContent.style.boxSizing = 'border-box'
    tempOverflowBadgeContent.style.borderRadius = '4px'
    tempOverflowBadgeContent.style.padding = '2px 8px'
    tempOverflowBadgeContent.style.fontSize = '12px'
    tempOverflowBadgeContent.style.lineHeight = '1'
    tempOverflowBadgeContent.textContent = '+99'
    tempOverflowBadgeWrapper.appendChild(tempOverflowBadgeContent)

    document.body.appendChild(tempOverflowBadgeWrapper)
    const overflowBadgeEstimatedWidth = tempOverflowBadgeWrapper.offsetWidth
    document.body.removeChild(tempOverflowBadgeWrapper)

    for (let i = 0; i < selectedOptions.length; i++) {
      const option = selectedOptions[i]
      const badgeWidth = badgeWidths[option.value.toString()]

      const remainingBadges = selectedOptions.length - (i + 1)
      const spaceNeededForOverflow =
        remainingBadges > 0 ? overflowBadgeEstimatedWidth : 0

      if (
        currentWidth + badgeWidth + spaceNeededForOverflow <=
        availableWidth
      ) {
        currentWidth += badgeWidth
        count++
      } else {
        break
      }
    }

    if (
      count === 0 &&
      selectedOptions.length > 0 &&
      overflowBadgeEstimatedWidth <= availableWidth
    ) {
      count = 0
    } else if (
      count > 0 &&
      selectedOptions.length - count > 0 &&
      currentWidth + overflowBadgeEstimatedWidth > availableWidth
    ) {
      count--
    }

    setVisibleBadgeCount(count)
  }, [values, options, leadIcon, dropdownRef.current?.offsetWidth])

  // ResizeObserver to react to width changes of the dropdown container
  useEffect(() => {
    const dropdownElement = dropdownRef.current
    if (!dropdownElement) return

    const observer = new ResizeObserver(() => {
      setVisibleBadgeCount(null)
    })

    observer.observe(dropdownElement)

    return () => observer.unobserve(dropdownElement)
  }, [])

  const filteredOptions = options.filter((option) => {
    if (filter && option.labelFilterText) {
      return option.labelFilterText
        .toLowerCase()
        .includes(filter.toLocaleLowerCase())
    }
    if (filter && typeof option.label === 'string') {
      return option.label.toLowerCase().includes(filter.toLocaleLowerCase())
    }
    return true
  })

  // const badgesToDisplay = selectedOptions.slice(0, visibleBadgeCount || 0)
  // const overflowCount = selectedOptions.length - (visibleBadgeCount || 0)

  return (
    <LabelDescription
      label={label}
      description={description}
      error={error && errorMessage}
    >
      <Menu onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
        <Menu.Trigger>
          <Dropdown
            ref={dropdownRef}
            onClick={() => {
              setFilter('')
            }}
            open={open}
            error={error}
            leadIcon={leadIcon}
            disabled={disabled}
            placeholder={placeholder}
            value={
              values.length > 0 ? (
                <div className="a gq bc"
                >
                  {selectedOptions.map((v) => (
                    <div className="go gp ek"
                      key={v.value?.toString()}
                    >
                      <Badge color={v.color || 'neutral-subtle'}>
                        {typeof v.label === 'string'
                          ? v.label
                          : String(v.value)}
                      </Badge>
                    </div>
                  ))}
                  {/* {overflowCount > 0 && <OverflowBadge count={overflowCount} />} */}
                </div>
              ) : undefined
            }
            style={{ ...style }}
          />
        </Menu.Trigger>
        <Menu.Items
          style={{
            padding: 0,
          }}
        >
          {shouldBeFilterable && (
            <div className="gr a ac fa dj gs dt"
              style={{
                left: -8,
                right: -8,
                background: `linear-gradient(${colors.neutral10Background}),
                linear-gradient(${colors.neutralInverted100})`,}}
            >
              <TextInput
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                }}
                variant="ghost"
                leadIcon="search"
                placeholder="Find..."
              />
              <Separator style={{ padding: 0, height: 1 }} />
            </div>
          )}
          <div className="fk"
          >
            {filteredOptions.map((option) => (
              <Menu.ToggleItem
                key={option.value?.toString()}
                keepOpenOnClick={true}
                toggled={values.includes(option.value)}
                onChange={() => {
                  if (values.includes(option.value)) {
                    onChange(values.filter((v) => v !== option.value))
                  } else {
                    onChange([...values, option.value])
                  }
                }}
              >
                {option.label}
              </Menu.ToggleItem>
            ))}
            {filteredOptions.length === 0 && (
              <Menu.Item
                label="No results"
                indentContent
                disabled
                style={{ pointerEvents: 'none' }}
              />
            )}
          </div>
          {!filter && (
            <div className="gr a ac eo aw gt f dt"
              style={{
                left: -8,
                right: -8,
                background: `linear-gradient(${colors.neutral10Background}),
                linear-gradient(${colors.neutralInverted100})`,}}
            >
              <Separator style={{ padding: 0, height: 1 }} />
              <Menu.Item
                label="Clear"
                color="red"
                leadIcon="close"
                onClick={() => onChange([])}
                style={{ width: '100%', marginTop: 6 }}
              />
            </div>
          )}
        </Menu.Items>
      </Menu>
    </LabelDescription>
  )
}

export { MultiSelectInput }
export type { MultiSelectInputProps }
